import { Loader2, Trash2 } from "lucide-react"
import * as React from "react"
import { toast } from "sonner"

import { PageHeader } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { useLocale, useT } from "@/i18n/provider"
import { api, ensureActiveWorkspace, toastApiError } from "@/lib/api"
import { useAuth } from "@/lib/auth"
import type { InviteRole, Workspace, WorkspaceInvite, WorkspaceMember, WorkspaceRole } from "@/lib/types"
import { formatDateTime } from "@/lib/utils"
import { getActiveWorkspaceId, setActiveWorkspaceId } from "@/lib/workspace"

const ROLE_RANK: Record<WorkspaceRole, number> = { member: 0, admin: 1, owner: 2 }

function roleBadgeVariant(role: WorkspaceRole): "default" | "secondary" | "outline" {
  if (role === "owner") return "default"
  if (role === "admin") return "secondary"
  return "outline"
}

export function WorkspaceSettingsPage() {
  const t = useT()
  const { locale } = useLocale()
  const { user } = useAuth()

  const [workspaces, setWorkspaces] = React.useState<Workspace[] | null>(null)
  const [activeId, setActiveId] = React.useState<string | null>(getActiveWorkspaceId())
  const [members, setMembers] = React.useState<WorkspaceMember[] | null>(null)
  const [invites, setInvites] = React.useState<WorkspaceInvite[] | null>(null)
  const [name, setName] = React.useState("")
  const [busy, setBusy] = React.useState<string | null>(null)
  const [inviteLink, setInviteLink] = React.useState<string | null>(null)
  const [linkCopied, setLinkCopied] = React.useState(false)

  const workspace = React.useMemo(
    () => workspaces?.find((w) => w.id === activeId) ?? workspaces?.[0] ?? null,
    [workspaces, activeId],
  )
  const role = workspace?.role ?? "member"
  const isAdmin = ROLE_RANK[role] >= ROLE_RANK.admin
  const isOwner = role === "owner"

  const loadWorkspaces = React.useCallback(async () => {
    await ensureActiveWorkspace()
    const list = await api.listWorkspaces()
    setWorkspaces(list)
    setActiveId(getActiveWorkspaceId())
    return list
  }, [])

  React.useEffect(() => {
    loadWorkspaces().catch((error) => toastApiError(error, t("workspaceSettings.toasts.loadFailed")))
  }, [loadWorkspaces, t])

  React.useEffect(() => {
    setName(workspace?.name ?? "")
  }, [workspace?.id, workspace?.name])

  const loadMembers = React.useCallback(
    async (workspaceId: string) => {
      try {
        setMembers(await api.listMembers(workspaceId))
      } catch (error) {
        toastApiError(error, t("workspaceSettings.toasts.membersLoadFailed"))
      }
    },
    [t],
  )

  const loadInvites = React.useCallback(
    async (workspaceId: string) => {
      try {
        setInvites(await api.listInvites(workspaceId))
      } catch (error) {
        toastApiError(error, t("workspaceSettings.toasts.invitesLoadFailed"))
      }
    },
    [t],
  )

  React.useEffect(() => {
    if (!workspace) return
    void loadMembers(workspace.id)
  }, [workspace, loadMembers])

  React.useEffect(() => {
    if (!workspace || !isAdmin) {
      setInvites(null)
      return
    }
    void loadInvites(workspace.id)
  }, [workspace, isAdmin, loadInvites])

  const ownerCount = React.useMemo(
    () => members?.filter((m) => m.role === "owner").length ?? 0,
    [members],
  )

  async function withBusy(label: string, work: () => Promise<void>) {
    setBusy(label)
    try {
      await work()
    } catch (error) {
      toastApiError(error, t("common.actionFailed"))
    } finally {
      setBusy(null)
    }
  }

  /** Point the app at a workspace that still exists, then reload so every
   *  already-fetched page refetches under it - same pattern the switcher
   *  uses when the active workspace changes. */
  async function switchAwayAndReload() {
    try {
      const remaining = await api.listWorkspaces()
      const next = remaining.find((w) => w.is_default) ?? remaining[0] ?? null
      setActiveWorkspaceId(next ? next.id : null)
    } catch {
      setActiveWorkspaceId(null)
    }
    window.location.reload()
  }

  async function saveName(event: React.FormEvent) {
    event.preventDefault()
    if (!workspace || !name.trim() || name === workspace.name) return
    await withBusy("rename", async () => {
      await api.updateWorkspace(workspace.id, name.trim())
      toast.success(t("workspaceSettings.toasts.renamed"))
      await loadWorkspaces()
    })
  }

  async function changeRole(member: WorkspaceMember, nextRole: WorkspaceRole) {
    if (!workspace || nextRole === member.role) return
    await withBusy(`role-${member.user_id}`, async () => {
      await api.updateMemberRole(workspace.id, member.user_id, nextRole)
      toast.success(t("workspaceSettings.toasts.roleUpdated", { email: member.email }))
      await loadMembers(workspace.id)
    })
  }

  async function removeMember(member: WorkspaceMember, isSelf: boolean) {
    if (!workspace) return
    const confirmMessage = isSelf
      ? t("workspaceSettings.leaveConfirm", { name: workspace.name })
      : t("workspaceSettings.removeConfirm", { email: member.email })
    if (!confirm(confirmMessage)) return
    await withBusy(`remove-${member.user_id}`, async () => {
      await api.removeMember(workspace.id, member.user_id)
      toast.success(
        isSelf
          ? t("workspaceSettings.toasts.left")
          : t("workspaceSettings.toasts.removed", { email: member.email }),
      )
      if (isSelf) {
        await switchAwayAndReload()
      } else {
        await loadMembers(workspace.id)
      }
    })
  }

  async function revokeInvite(invite: WorkspaceInvite) {
    if (!workspace) return
    await withBusy(`revoke-${invite.id}`, async () => {
      await api.revokeInvite(workspace.id, invite.id)
      toast.success(t("workspaceSettings.toasts.inviteRevoked"))
      if (inviteLink) setInviteLink(null)
      await loadInvites(workspace.id)
    })
  }

  async function resendInvite(invite: WorkspaceInvite) {
    if (!workspace) return
    await withBusy(`resend-${invite.id}`, async () => {
      const created = await api.resendInvite(workspace.id, invite.id)
      setInviteLink(created.accept_url)
      setLinkCopied(false)
      toast.success(t("workspaceSettings.toasts.inviteResent", { email: invite.email }))
      await loadInvites(workspace.id)
    })
  }

  async function copyInviteLink() {
    if (!inviteLink) return
    try {
      await navigator.clipboard.writeText(inviteLink)
      setLinkCopied(true)
    } catch {
      /* clipboard blocked - the field stays selectable */
    }
  }

  async function deleteWorkspace() {
    if (!workspace) return
    if (!confirm(t("workspaceSettings.deleteConfirm", { name: workspace.name }))) return
    await withBusy("delete", async () => {
      await api.deleteWorkspace(workspace.id)
      toast.success(t("workspaceSettings.toasts.deleted"))
      await switchAwayAndReload()
    })
  }

  if (workspaces === null) {
    return (
      <>
        <PageHeader
          title={t("workspaceSettings.title")}
          description={t("workspaceSettings.description")}
        />
        <div className="space-y-4 p-6">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </>
    )
  }

  if (!workspace) return null

  return (
    <>
      <PageHeader
        title={t("workspaceSettings.title")}
        description={t("workspaceSettings.description")}
      />
      <div className="space-y-6 p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("workspaceSettings.general.title")}</CardTitle>
            <CardDescription>{t("workspaceSettings.general.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isAdmin ? (
              <form onSubmit={saveName} className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1 space-y-2">
                  <Label htmlFor="workspace-name">{t("common.name")}</Label>
                  <Input
                    id="workspace-name"
                    required
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy !== null || !name.trim() || name === workspace.name}
                >
                  {busy === "rename" && <Loader2 className="size-4 animate-spin" />}
                  {t("workspaceSettings.general.save")}
                </Button>
              </form>
            ) : (
              <div className="space-y-2">
                <Label>{t("common.name")}</Label>
                <p className="text-sm">{workspace.name}</p>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{t("workspaceSettings.yourRole")}</span>
              <Badge variant={roleBadgeVariant(role)}>{t(`workspaceSettings.roles.${role}`)}</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("workspaceSettings.members.title")}</CardTitle>
            <CardDescription>{t("workspaceSettings.members.description")}</CardDescription>
          </CardHeader>
          <CardContent>
            {members === null ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">
                        {t("workspaceSettings.members.columnMember")}
                      </th>
                      <th className="px-4 py-2 text-left font-medium">
                        {t("workspaceSettings.members.columnRole")}
                      </th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => {
                      const isSelf = member.user_id === user?.id
                      const isLastOwner = member.role === "owner" && ownerCount <= 1
                      const canTouchRole = isAdmin && (member.role !== "owner" || isOwner) && !isLastOwner
                      const canRemoveOther =
                        isAdmin && !isSelf && (member.role !== "owner" || isOwner) && !isLastOwner
                      const rowBusy =
                        busy === `role-${member.user_id}` || busy === `remove-${member.user_id}`
                      return (
                        <tr key={member.user_id} className="border-t">
                          <td className="px-4 py-2">
                            <div className="font-medium">{member.display_name ?? member.email}</div>
                            {member.display_name && (
                              <div className="text-muted-foreground text-xs">{member.email}</div>
                            )}
                          </td>
                          <td className="px-4 py-2">
                            {canTouchRole ? (
                              <Select
                                value={member.role}
                                onValueChange={(value) => changeRole(member, value as WorkspaceRole)}
                              >
                                <SelectTrigger className="h-8 w-28 text-xs" disabled={rowBusy}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="member">
                                    {t("workspaceSettings.roles.member")}
                                  </SelectItem>
                                  <SelectItem value="admin">
                                    {t("workspaceSettings.roles.admin")}
                                  </SelectItem>
                                  <SelectItem value="owner" disabled={!isOwner}>
                                    {t("workspaceSettings.roles.owner")}
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge variant={roleBadgeVariant(member.role)}>
                                {t(`workspaceSettings.roles.${member.role}`)}
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {isSelf ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                disabled={busy !== null || isLastOwner}
                                title={isLastOwner ? t("workspaceSettings.lastOwnerHint") : undefined}
                                onClick={() => removeMember(member, true)}
                              >
                                {t("workspaceSettings.leave")}
                              </Button>
                            ) : canRemoveOther ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive"
                                disabled={busy !== null}
                                onClick={() => removeMember(member, false)}
                              >
                                <Trash2 className="size-4" />
                                {t("workspaceSettings.members.remove")}
                              </Button>
                            ) : null}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle>{t("workspaceSettings.invite.title")}</CardTitle>
              <CardDescription>{t("workspaceSettings.invite.description")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <InviteForm
                workspaceId={workspace.id}
                onInvited={() => loadInvites(workspace.id)}
                onLink={(url) => {
                  setInviteLink(url)
                  setLinkCopied(false)
                }}
              />

              {inviteLink && (
                <div className="bg-muted/40 space-y-2 rounded-md border p-3">
                  <p className="text-muted-foreground text-sm">
                    {t("workspaceSettings.invite.linkHint")}
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Input
                      readOnly
                      value={inviteLink}
                      onFocus={(e) => e.currentTarget.select()}
                      className="flex-1 font-mono text-xs"
                    />
                    <Button type="button" variant="secondary" onClick={copyInviteLink}>
                      {linkCopied
                        ? t("workspaceSettings.invite.copied")
                        : t("workspaceSettings.invite.copy")}
                    </Button>
                  </div>
                </div>
              )}

              {invites === null ? (
                <Skeleton className="h-20 w-full" />
              ) : invites.length === 0 ? (
                <p className="text-muted-foreground text-sm">{t("workspaceSettings.invite.empty")}</p>
              ) : (
                <ul className="divide-y rounded-lg border">
                  {invites.map((invite) => (
                    <li
                      key={invite.id}
                      className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm"
                    >
                      <div>
                        <span className="font-medium">{invite.email}</span>{" "}
                        <Badge variant={roleBadgeVariant(invite.role)} className="ml-1">
                          {t(`workspaceSettings.roles.${invite.role}`)}
                        </Badge>
                        <div className="text-muted-foreground text-xs">
                          {t("workspaceSettings.invite.expires", {
                            when: formatDateTime(invite.expires_at, locale),
                          })}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={busy === `resend-${invite.id}`}
                          onClick={() => resendInvite(invite)}
                        >
                          {busy === `resend-${invite.id}` && (
                            <Loader2 className="size-4 animate-spin" />
                          )}
                          {t("workspaceSettings.invite.resend")}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          disabled={busy === `revoke-${invite.id}`}
                          onClick={() => revokeInvite(invite)}
                        >
                          {t("workspaceSettings.invite.revoke")}
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        )}

        {isOwner && (
          <Card className="border-destructive/40">
            <CardHeader>
              <CardTitle>{t("workspaceSettings.danger.title")}</CardTitle>
              <CardDescription>{t("workspaceSettings.danger.description")}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button variant="destructive" disabled={busy !== null} onClick={deleteWorkspace}>
                {busy === "delete" && <Loader2 className="size-4 animate-spin" />}
                {t("workspaceSettings.danger.delete")}
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  )
}

function InviteForm({
  workspaceId,
  onInvited,
  onLink,
}: {
  workspaceId: string
  onInvited: () => Promise<void>
  onLink: (url: string) => void
}) {
  const t = useT()
  const [email, setEmail] = React.useState("")
  const [role, setRole] = React.useState<InviteRole>("member")
  const [busy, setBusy] = React.useState(false)

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      const created = await api.createInvite(workspaceId, email, role)
      toast.success(t("workspaceSettings.toasts.inviteSent", { email }))
      onLink(created.accept_url)
      setEmail("")
      setRole("member")
      await onInvited()
    } catch (error) {
      toastApiError(error, t("workspaceSettings.toasts.inviteFailed"))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <div className="min-w-0 flex-1 space-y-2">
        <Label htmlFor="invite-email">{t("workspaceSettings.invite.emailLabel")}</Label>
        <Input
          id="invite-email"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@example.com"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="invite-role">{t("workspaceSettings.invite.roleLabel")}</Label>
        <Select value={role} onValueChange={(value) => setRole(value as InviteRole)}>
          <SelectTrigger id="invite-role" className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="member">{t("workspaceSettings.roles.member")}</SelectItem>
            <SelectItem value="admin">{t("workspaceSettings.roles.admin")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={busy}>
        {busy && <Loader2 className="size-4 animate-spin" />}
        {t("workspaceSettings.invite.send")}
      </Button>
    </form>
  )
}
